import { useState } from "react";
import { useAppSelector } from "../../redux/store/hooks";
import {
  ROLE_IDS,
  ROLE_PROFILES,
  VISIBILITY_TEXT,
  CAPABILITY_SECTIONS,
  OWN_ONLY_QUALIFIER,
  levelFor,
  type RoleId,
  type CapabilityLevel,
} from "../../config/permissions";
import { IconCheck } from "../../components/icons";

// What each organization role can see and do. Data lives in
// config/permissions.ts. Opens on the viewer's own role.
const LEVEL_LABEL: Record<CapabilityLevel, string> = {
  full: "Full access",
  own: "Allowed, but limited to records you created",
  none: "Not available for this role",
};

const RolesPermissions = () => {
  const sessionRole = (
    useAppSelector((s) => s.layout.user?.role) ?? ""
  ).toLowerCase();
  const myRole = ROLE_IDS.includes(sessionRole as RoleId)
    ? (sessionRole as RoleId)
    : undefined;

  const [selected, setSelected] = useState<RoleId>(myRole ?? "owner");
  const [legendOpen, setLegendOpen] = useState(false);
  const profile = ROLE_PROFILES[selected];

  return (
    <div className="roles-perms">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Roles &amp; Permissions</h2>
            <p className="panel__sub">
              What each role in your organization can see and do. Pick a role to
              see its access.
            </p>
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setLegendOpen((open) => !open)}
            aria-expanded={legendOpen}
          >
            {legendOpen ? "Hide key" : "What the marks mean"}
          </button>
        </div>

        <div className="panel__body">
          {legendOpen ? (
            <div className="roles-perms__legend">
              {(["full", "own", "none"] as CapabilityLevel[]).map((level) => (
                <div className="roles-perms__legend-row" key={level}>
                  <span className={`roles-perms__mark roles-perms__mark--${level}`}>
                    {level === "full" ? <IconCheck /> : level === "own" ? "◑" : "—"}
                  </span>
                  <span>{LEVEL_LABEL[level]}</span>
                </div>
              ))}
            </div>
          ) : null}

          <div className="roles-perms__chips">
            {ROLE_IDS.map((id) => (
              <button
                type="button"
                key={id}
                className={`roles-perms__chip${
                  id === selected ? " roles-perms__chip--active" : ""
                }`}
                onClick={() => setSelected(id)}
              >
                {ROLE_PROFILES[id].name}
                {id === myRole ? (
                  <span className="roles-perms__you">YOU</span>
                ) : null}
              </button>
            ))}
          </div>

          <div className="roles-perms__summary">
            <h3>{profile.name}</h3>
            <p className="panel__sub">{profile.summary}</p>
            <div
              className={`roles-perms__visibility roles-perms__visibility--${profile.visibility}`}
            >
              <strong>What you can see</strong>
              <p>{VISIBILITY_TEXT[profile.visibility]}</p>
            </div>
          </div>
        </div>
      </section>

      {CAPABILITY_SECTIONS.map((section) => (
        <section className="panel" key={section.id}>
          <div className="panel__head">
            <div>
              <h2>{section.title}</h2>
            </div>
          </div>
          <div className="panel__body">
            {section.capabilities.map((cap) => {
              const level = levelFor(cap, selected);
              return (
                <div
                  className={`roles-perms__row roles-perms__row--${level}`}
                  key={cap.id}
                >
                  <span className={`roles-perms__mark roles-perms__mark--${level}`}>
                    {level === "full" ? <IconCheck /> : level === "own" ? "◑" : "—"}
                  </span>
                  <div>
                    <p className="roles-perms__label">{cap.label}</p>
                    {level === "own" ? (
                      <p className="roles-perms__qualifier">
                        {cap.qualifier ?? OWN_ONLY_QUALIFIER}
                      </p>
                    ) : null}
                    {cap.note && level !== "none" ? (
                      <p className="roles-perms__note">{cap.note}</p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <p className="roles-perms__footnote">
        Platform Owner and Broker are not organization roles — they sit outside
        your organization and are not listed here. Access is always enforced by
        the server, so this screen describes it rather than controls it.
      </p>
    </div>
  );
};

export default RolesPermissions;
